import { Empty, Flex, hideScrollBar, Modal, Text, Toast } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import type { OtherPlatformType } from '@lighthouse/shared'
import { useAtomAction, useAtomData } from '@lighthouse/shared'
import { find, reduce } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { useSetState } from 'react-use'
import styled from 'styled-components'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import * as srv from '@/services'

import { INTEGRATION_LIST, otherPlatformNameMap } from './constant'
import { OtherPlatformsHeader } from './Header'
import { openOtherPlatform } from './OtherPlatformEvent'
import { OtherPlatformPanel } from './RightPanel'
import { OtherPlatformsTable } from './Table'
import type { IntegrationKindList, IntegrationList, State } from './types'

interface OtherPlatformsProps {}

const SCxContainer = styled.div`
    width: 100%;
    height: 100%;
    /* padding: 0 24px; */
    display: flex;
    flex-direction: column;
    position: relative;
    gap: 20px;
    background-color: var(--color-gray-50);
`

const SCxEmptyImage = styled.img`
    width: 195px;
    height: 194px;
`

const SCxScrollBar = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 50px;
    overflow-y: auto;
    ${hideScrollBar}
`
const SCxEmptyDescribe = styled.div`
    line-height: 22px;
    font-size: var(--font-size-normal);
`
const emptyImage = getAssetUrl('empty', 'empty_applications.svg')

export const OtherPlatforms: React.FunctionComponent<OtherPlatformsProps> = props => {
    const otherPlatformList = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)

    const [{ searchWord, activeType, lookType }, setState] = useSetState<State>({
        searchWord: '',
        lookType: 'alreadyInstall'
    })

    const list: IntegrationList = useMemo(() => {
        if (lookType === 'pluginStore') {
            return INTEGRATION_LIST
        }
        return reduce<IntegrationKindList, IntegrationList>(
            (acc, item) => {
                const filteredItems = item.list.filter(subItem => otherPlatformList?.some(v => v.type === subItem.type))
                if (filteredItems.length > 0) {
                    acc.push({ ...item, list: filteredItems })
                }
                return acc
            },
            [],
            INTEGRATION_LIST
        )
    }, [lookType, otherPlatformList])

    const filterList = useMemo(() => {
        if (!searchWord) {
            return list
        }
        return reduce<IntegrationKindList, IntegrationList>(
            (acc, item) => {
                const filteredItems = item.list.filter(subItem =>
                    subItem.title.toLocaleLowerCase().includes(searchWord.toLocaleLowerCase())
                )
                if (filteredItems.length > 0) {
                    acc.push({ ...item, list: filteredItems })
                }
                return acc
            },
            [],
            list
        )
    }, [searchWord, list])

    const handleDelete = useCallback(
        async (type: OtherPlatformType) => {
            if (type === 'WECHAT_PAY') {
                openOtherPlatform({ type })
                return
            }
            const integration = find(v => v.type === type, otherPlatformList ?? [])
            if (!integration) {
                return
            }
            const isConfirm = await Modal.confirm({
                title: '确认移除',
                content: `移除「${otherPlatformNameMap[type]}」插件，会使相关配置数据被清空，导致相关流程节点或其他使用该配置的功能异常。`,
                okStatus: 'error',
                okText: '移除'
            })
            if (isConfirm) {
                const isSuccess = await srv.deleteIntegration(integration.id)
                if (isSuccess) {
                    fetchOtherPlatforms()
                    Toast.success('移除成功')
                    setState({ activeType: undefined })
                }
            }
        },
        [otherPlatformList, setState, fetchOtherPlatforms]
    )

    const handleInstallIntegration = useCallback(
        async (type: OtherPlatformType) => {
            if (type === 'WECHAT_PAY' && otherPlatformList?.some(item => item.type === type)) {
                openOtherPlatform({ type })
                return
            }
            const res = await srv.addIntegration(type)
            if (res) {
                fetchOtherPlatforms()
                Toast.success(`${otherPlatformNameMap[type]}安装成功，`, {
                    linkText: '查看>',
                    onLinkClick: () => {
                        setState({ lookType: 'alreadyInstall', activeType: type, searchWord: '' })
                    }
                })
            }
        },
        [setState, fetchOtherPlatforms, otherPlatformList]
    )

    const handleSelect = useCallback(
        (type?: OtherPlatformType) => {
            if (lookType === 'alreadyInstall') {
                setState({
                    activeType: type
                })
            }
        },
        [lookType, setState]
    )

    const handlePerfect = useCallback((type: OtherPlatformType) => {
        openOtherPlatform({ type })
    }, [])

    return (
        <SCxContainer>
            <Flex style={{ width: '100%', height: '100%' }}>
                <SCxScrollBar>
                    <OtherPlatformsHeader
                        searchWord={searchWord}
                        lookType={lookType}
                        onSearchChange={val => setState({ searchWord: val })}
                        onLookTypeChange={v => setState({ lookType: v, searchWord: '', activeType: undefined })}
                    />
                    {filterList.length > 0 ? (
                        <>
                            <OtherPlatformsTable
                                activeType={activeType}
                                list={filterList}
                                lookType={lookType}
                                onSelect={handleSelect}
                                onInstall={handleInstallIntegration}
                            />
                        </>
                    ) : (
                        <Empty
                            icon={<SCxEmptyImage src={emptyImage} />}
                            style={{ flex: 1 }}
                            description={
                                <SCxEmptyDescribe>
                                    <Text>找不到插件</Text>
                                    {lookType === 'alreadyInstall' && (
                                        <Text>
                                            请前往{' '}
                                            <span
                                                style={{ color: 'var(--color-theme-7)', padding: '0 3px', cursor: 'pointer' }}
                                                onClick={() => setState({ lookType: 'pluginStore', searchWord: '' })}
                                            >
                                                插件市场
                                            </span>
                                            安装
                                        </Text>
                                    )}
                                </SCxEmptyDescribe>
                            }
                        />
                    )}
                </SCxScrollBar>
                {activeType && list.length > 0 && (
                    <OtherPlatformPanel type={activeType} onPerfect={handlePerfect} onSelect={handleSelect} onDelete={handleDelete} />
                )}
            </Flex>
        </SCxContainer>
    )
}
